import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ajuda as api, type AjudaDaRota } from '../api/ajuda'
import { Estado } from './Estado'
import { Verbete } from './Verbete'
import { marcaDaRota } from '../paginas/marcaDaRota'
import '../paginas/ajuda.css'

/**
 * O "?" da tela — a ajuda de onde a pessoa está.
 *
 * 🔴 **É este componente que faz a demanda 040 reduzir suporte.** Uma FAQ que só
 * existe numa página separada é uma FAQ que ninguém abre: a dúvida nasce dentro
 * da tela, e é ali que a resposta precisa estar. A página de ajuda serve para
 * estudar; isto serve para desempacar.
 *
 * Some sozinho quando não há verbete para a rota — botão que abre um painel
 * vazio ensina a não clicar mais.
 */
export function AjudaDaTela({ caminho }: { caminho?: string }) {
  const { t } = useTranslation()

  const rota = caminho ?? window.location.pathname
  const marca = marcaDaRota() ?? undefined

  const [conteudo, setConteudo] = useState<AjudaDaRota | null>(null)
  const [aberto, setAberto] = useState(false)
  const [falhou, setFalhou] = useState(false)
  const painel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let vivo = true

    api
      .porRota(rota)
      .then((r) => vivo && setConteudo(r))
      .catch(() => vivo && setFalhou(true))

    return () => {
      vivo = false
    }
  }, [rota])

  // Esc fecha. Painel que só fecha com o mouse prende quem usa teclado.
  useEffect(() => {
    if (!aberto) return

    function aoTeclar(evento: KeyboardEvent) {
      if (evento.key === 'Escape') setAberto(false)
    }

    document.addEventListener('keydown', aoTeclar)
    return () => document.removeEventListener('keydown', aoTeclar)
  }, [aberto])

  // Falha ao consultar a ajuda não pode atrapalhar a tela que a hospeda: some.
  if (falhou || !conteudo || conteudo.total === 0) return null

  return (
    <div className="ajudaDaTela">
      <button
        type="button"
        className="ajudaDaTela__botao"
        aria-expanded={aberto}
        aria-label={t('ajuda.abrirDaTela', { total: conteudo.total })}
        onClick={() => setAberto(!aberto)}
      >
        ?
      </button>

      {aberto && (
        <div ref={painel} className="ajudaDaTela__painel" role="dialog" aria-label={t('ajuda.tituloDaTela')}>
          <p className="ajudaDaTela__titulo">{t('ajuda.tituloDaTela')}</p>

          {conteudo.verbetes.length === 0 ? (
            <Estado tipo="vazio" descricao={t('ajuda.semVerbeteDaTela')} />
          ) : (
            conteudo.verbetes.map((v) => (
              <article key={v.id}>
                <h3 className="ajudaDaTela__titulo">{v.titulo}</h3>
                <Verbete blocos={v.blocos} marca={marca} />
              </article>
            ))
          )}

          <a href="/ajuda">{t('ajuda.verTudo')}</a>
        </div>
      )}
    </div>
  )
}
